import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Event, NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService, isNullOrUndefined, Message, MessageService, RestI18nLoader } from '@sla-ng/core';
import { ISlairUser } from '@sla-ng/slair-core';
import { MenuItem } from '@sla-ng/ui-material';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, filter } from 'rxjs';
import * as  packageJson from './../../../../package.json';
import * as versionJson from './../assets/version.json';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: [ './app.component.scss' ],
})
export class AppComponent implements OnInit {

    public title: string = 'angular-app';

    public menuItems: MenuItem[] = [];
    // eslint-disable-next-line
    public packageJson: any = packageJson;
    public version: { version: string, timestamp: string } = versionJson[ 'default' ];

    public currentApplication$: BehaviorSubject<string> = new BehaviorSubject('');

    public currentUser$: BehaviorSubject<string> = new BehaviorSubject<string>('');

    constructor(private myRouter: Router,
                private myRestI18nLoader: RestI18nLoader,
                private myToastrService: ToastrService,
                private myMessageService: MessageService,
                private myTranslateService: TranslateService,
                private myChangeDetectorRef: ChangeDetectorRef,
                private myAuthService: AuthenticationService) {
        this.initializeTranslations();
        this.getMenuTitle();
    }

    public ngOnInit(): void {
        this.handleUserState();
        this.setupTranslations();
        this.listenToMessageService();
    }

    public handleUserState(): void {
        // Subscribe to authentication changes
        this.myAuthService.user$.subscribe((user) => {
            const authBean = user as ISlairUser;
            this.currentUser$.next(authBean?.email);

            if (!isNullOrUndefined(user)) {
                this.createMenu();
            }
        });
    }



    public onMenuItemClicked(menuItem: MenuItem): void {
        if (!isNullOrUndefined(menuItem[ 'label' ])) {
            this.currentApplication$.next(menuItem[ 'label' ]);
        }
    }

    /*
    *@brief  find MenuTitle and exchange with currentApplication-Update
    */
    public getMenuTitle(): void {
        this.myRouter.events.pipe(
            filter((event: Event) => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError),
        ).subscribe((event: NavigationEnd | NavigationCancel | NavigationError) => {
            const currentUrl: string = (event instanceof NavigationEnd && event.urlAfterRedirects) ? event.urlAfterRedirects : event.url; // complete URL with ID
            let trimmedUrl = currentUrl.split('?')[ 0 ];  // routerLink

            // Remove IDs from path
            const regex = /\/\d+$/;
            if (regex.test(trimmedUrl)) {
                trimmedUrl = trimmedUrl.replace(regex, '');
            }

            const foundItem = this.findMenuItemByRouterLink(this.menuItems, trimmedUrl);
            if (foundItem) {
                this.currentApplication$.next(foundItem.label);
            } else {
                console.log(`No menu item found for ${currentUrl}`);
            }
        });
    }

    /*
    *@brief  find MenuItem with the routerLink
    *@param  menuItem-List, current routerLink
    *@return  menuItem, if found/existent, else undefined
    */
    private findMenuItemByRouterLink(menuItems: MenuItem[], routerLink: string): MenuItem | undefined {
        for (const item of menuItems) {
            if (item.routerLink === routerLink || routerLink.split('/')[ 0 ] === item.routerLink) {
                return item;
            }
            if (item.subItems) {
                const found = this.findMenuItemByRouterLink(item.subItems, routerLink);
                if (found) {
                    return found;
                }
            }
        }
        return undefined;
    }

    private initializeTranslations(): void {
        this.myTranslateService.addLangs([ 'en', 'de' ]);
        this.myTranslateService.setDefaultLang('en');

        const browserLang = this.myTranslateService.getBrowserLang();
        this.myTranslateService.use((/en|de/.exec(browserLang)) ? browserLang : 'de');
    }

    private setupTranslations(): void {
        this.myTranslateService.setDefaultLang('en');
        this.myTranslateService.use('de');
    }

    private listenToMessageService(): void {
        this.myMessageService.getMessage().subscribe((message: Message) => {
            switch (message.severity) {
                case 'success':
                    this.myToastrService.success(message.detail, undefined, {
                        timeOut: 3000,
                        closeButton: true,
                        progressBar: true,
                        progressAnimation: 'increasing',
                    });
                    break;
                case 'warn':
                    this.myToastrService.warning(message.detail, undefined, {
                        timeOut: 5000,
                        closeButton: true,
                        progressBar: true,
                        progressAnimation: 'increasing',
                    });
                    break;
                case 'error':
                    this.myToastrService.error(message.detail, message.summary, {
                        disableTimeOut: true,
                        tapToDismiss: true,
                        closeButton: true,
                        progressBar: true,
                        progressAnimation: 'increasing',
                    });
                    break;
                case 'info':
                    this.myToastrService.info(message.detail, undefined, {
                        timeOut: 5000,
                        closeButton: true,
                        progressBar: true,
                        progressAnimation: 'increasing',
                    });
                    break;
            }

            this.myChangeDetectorRef.markForCheck();
        });
    }

    private createMenu(): void {
        this.menuItems = [
            {
                label: 'app.menu.master-data.label',
                icon: 'list',
                routerLink: '/master-data',
                subItems: [
                    {
                        label: 'app.menu.master-data.measure-unit.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/measure-unit',
                    },
                    {
                        label: 'app.menu.master-data.reference-size.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/reference-size',
                    },
                    {
                        label: 'app.menu.master-data.costing-levels.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/costing-levels',
                    },
                    {
                        label: 'app.menu.master-data.cost-type.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/cost-type',
                    },
                    {
                        label: 'app.menu.master-data.cost-center.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/cost-center',
                    },
                    {
                        label: 'app.menu.master-data.join-cost-type-cost-center.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/join-cost-type-cost-center',
                    },
                    {
                        label: 'app.menu.master-data.cost-unit.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/cost-unit',
                    },
                    {
                        label: 'app.menu.master-data.cost-unit-group.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/cost-unit-group',
                    },
                    {
                        label: 'app.menu.master-data.join-cost-center-cost-unit.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/join-cost-center-cost-unit',
                    },
                    {
                        label: 'app.menu.master-data.contribution-margin-level.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/contribution-margin-level',
                    },
                    {
                        label: 'app.menu.master-data.bab-definition.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/bab-definition',
                    },
                    {
                        label: 'app.menu.master-data.cost-accounting-type.label',
                        icon: 'horizontal_rule',
                        routerLink: '/master-data/cost-accounting-type',
                    },
                ],
            },
            {
                label: 'app.menu.external-data.label',
                icon: 'list',
                routerLink: '/external-data',
                subItems: [
                    {
                        label: 'app.menu.external-data.recipe.label',
                        icon: 'horizontal_rule',
                        routerLink: '/external-data/production-version',
                    },
                    {
                        label: 'app.menu.external-data.production.label',
                        icon: 'horizontal_rule',
                        routerLink: '/external-data/production',
                    },
                    {
                        label: 'app.menu.external-data.article.label',
                        icon: 'horizontal_rule',
                        routerLink: '/external-data/article',
                    },
                    {
                        label: 'app.menu.external-data.purchase.label',
                        icon: 'horizontal_rule',
                        routerLink: '/external-data/purchase',
                    },
                    {
                        label: 'app.menu.external-data.moving-average-price.label',
                        icon: 'horizontal_rule',
                        routerLink: '/external-data/moving-average-price',
                    },
                    {
                        label: 'app.menu.external-data.time-record.label',
                        icon: 'horizontal_rule',
                        routerLink: '/external-data/time-record',
                    },
                    {
                        label: 'app.menu.external-data.sale.label',
                        icon: 'horizontal_rule',
                        routerLink: '/external-data/sale',
                    },
                    {
                        label: 'app.menu.external-data.planned-sale-price.label',
                        icon: 'horizontal_rule',
                        routerLink: '/external-data/planned-sale-price',
                    },
                    {
                        label: 'app.menu.external-data.cost.label',
                        icon: 'horizontal_rule',
                        routerLink: '/external-data/cost',
                    },
                    {
                        label: 'app.menu.external-data.article-costing-level.label',
                        icon: 'horizontal_rule',
                        routerLink: '/external-data/article-costing-level',
                    },
                    {
                        label: 'app.menu.external-data.working-plan.label',
                        icon: 'horizontal_rule',
                        routerLink: '/external-data/working-plan',
                    },
                ],
            },
            {
                label: 'app.menu.planning.label',
                icon: 'list',
                subItems: [
                    {
                        label: 'app.menu.planning.purchase-price-planning.label',
                        icon: 'horizontal_rule',
                        routerLink: '/planning/planned-purchase-price',
                    },
                    {
                        label: 'app.menu.planning.sale-volume-planning.label',
                        icon: 'horizontal_rule',
                        routerLink: '/planning/planned-sale-volume',
                    },
                ],
            },
            {
                label: 'app.menu.cost-accounting.label',
                icon: 'functions',
                routerLink: '/cost-accounting',
            },
            {
                label: 'app.menu.analysis.label',
                icon: 'insights',
                routerLink: '/cost-unit-cost',
            },

        ];
    }
}

